/**
 * ChallengeBanner Component
 * Shows pending game challenges as a banner on screens
 */

import React from "react";
import { View, Text, TouchableOpacity, Image, ScrollView } from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { useRouter } from "expo-router";
import { GameChallenge } from "../types";
import { useGameChallenge } from "../hooks/useGameChallenge";

interface ChallengeBannerProps {
  /** Custom class names */
  className?: string;
  /** Maximum challenges to show */
  maxVisible?: number;
  /** Compact variant for smaller spaces */
  compact?: boolean;
  /** Callback when a challenge is tapped */
  onChallengeTap?: (challenge: GameChallenge) => void;
}

/**
 * Banner showing pending game challenges
 */
export const ChallengeBanner: React.FC<ChallengeBannerProps> = ({
  className = "",
  maxVisible = 3,
  compact = false,
  onChallengeTap,
}) => {
  const router = useRouter();
  const { receivedChallenges } = useGameChallenge();

  // Filter to pending challenges only
  const pendingChallenges = receivedChallenges.slice(0, maxVisible);

  if (pendingChallenges.length === 0) {
    return null;
  }

  const handleChallengeTap = (challenge: GameChallenge) => {
    if (onChallengeTap) {
      onChallengeTap(challenge);
    } else {
      router.push("/games/uitdagingen");
    }
  };

  const handleViewAll = () => {
    router.push("/games/uitdagingen");
  };

  if (compact) {
    // Compact single-line version
    return (
      <TouchableOpacity
        onPress={handleViewAll}
        className={`flex-row items-center bg-purple-100 dark:bg-purple-900/30 px-4 py-3 rounded-xl ${className}`}
        accessibilityLabel={`${pendingChallenges.length} uitdagingen wachten`}
        accessibilityRole="button"
      >
        <View className="w-8 h-8 bg-purple-500 rounded-full items-center justify-center">
          <Ionicons name="game-controller" size={18} color="#FFFFFF" />
        </View>
        <View className="flex-1 ml-3">
          <Text className="font-semibold text-purple-800 dark:text-purple-200">
            {pendingChallenges.length} uitdaging{pendingChallenges.length > 1 ? "en" : ""} wachten!
          </Text>
          <Text className="text-sm text-purple-600 dark:text-purple-400">
            van {pendingChallenges[0].challenger?.full_name}
            {pendingChallenges.length > 1 && ` en ${pendingChallenges.length - 1} andere${pendingChallenges.length > 2 ? "n" : ""}`}
          </Text>
        </View>
        <Ionicons name="chevron-forward" size={20} color="#7C3AED" />
      </TouchableOpacity>
    );
  }

  // Full banner with individual challenge cards
  return (
    <View className={`${className}`}>
      {/* Header */}
      <View className="flex-row items-center justify-between mb-3">
        <View className="flex-row items-center">
          <View className="w-8 h-8 bg-purple-500 rounded-full items-center justify-center">
            <Ionicons name="game-controller" size={18} color="#FFFFFF" />
          </View>
          <Text className="ml-2 font-bold text-gray-900 dark:text-white">
            Uitdagingen
          </Text>
          <View className="ml-2 bg-purple-500 px-2 py-0.5 rounded-full">
            <Text className="text-xs text-white font-bold">
              {receivedChallenges.length}
            </Text>
          </View>
        </View>
        {receivedChallenges.length > maxVisible && (
          <TouchableOpacity
            onPress={handleViewAll}
            className="flex-row items-center"
            accessibilityLabel="Bekijk alle uitdagingen"
            accessibilityRole="link"
          >
            <Text className="text-purple-600 dark:text-purple-400 font-medium">
              Bekijk alle
            </Text>
            <Ionicons name="chevron-forward" size={16} color="#7C3AED" />
          </TouchableOpacity>
        )}
      </View>

      {/* Challenge Cards */}
      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        className="-mx-1"
        contentContainerStyle={{ paddingHorizontal: 4 }}
      >
        {pendingChallenges.map((challenge) => (
          <ChallengeCard
            key={challenge.id}
            challenge={challenge}
            onPress={() => handleChallengeTap(challenge)}
          />
        ))}
      </ScrollView>
    </View>
  );
};

/**
 * Individual challenge card
 */
interface ChallengeCardProps {
  challenge: GameChallenge;
  onPress: () => void;
}

const ChallengeCard: React.FC<ChallengeCardProps> = ({ challenge, onPress }) => {
  const challenger = challenge.challenger;
  const game = challenge.game;

  // Calculate time remaining
  const expiresAt = new Date(challenge.expires_at);
  const now = new Date();
  const hoursRemaining = Math.max(0, Math.floor((expiresAt.getTime() - now.getTime()) / (1000 * 60 * 60)));

  return (
    <TouchableOpacity
      onPress={onPress}
      className="bg-white dark:bg-gray-800 rounded-xl p-3 mr-3 shadow-sm border border-gray-100 dark:border-gray-700"
      style={{ width: 200 }}
      accessibilityLabel={`Uitdaging van ${challenger?.full_name} voor ${game?.name}`}
      accessibilityRole="button"
    >
      {/* Game icon & type */}
      <View className="flex-row items-center mb-2">
        <View
          className="w-8 h-8 rounded-lg items-center justify-center"
          style={{ backgroundColor: game?.color || "#7C3AED" }}
        >
          <Ionicons
            name={(game?.icon as keyof typeof Ionicons.glyphMap) || "game-controller"}
            size={18}
            color="#FFFFFF"
          />
        </View>
        <Text className="ml-2 font-semibold text-gray-800 dark:text-white">
          {game?.name || "Spel"}
        </Text>
      </View>

      {/* Challenger */}
      <View className="flex-row items-center mb-2">
        {challenger?.avatar_url ? (
          <Image
            source={{ uri: challenger.avatar_url }}
            className="w-6 h-6 rounded-full"
          />
        ) : (
          <View className="w-6 h-6 rounded-full bg-gray-200 dark:bg-gray-600 items-center justify-center">
            <Text className="text-xs font-bold text-gray-600 dark:text-gray-300">
              {challenger?.full_name?.charAt(0) || "?"}
            </Text>
          </View>
        )}
        <Text className="ml-2 text-sm text-gray-600 dark:text-gray-300" numberOfLines={1}>
          {challenger?.full_name || "Iemand"}
        </Text>
      </View>

      {/* Message if present */}
      {challenge.message && (
        <Text
          className="text-xs text-gray-500 dark:text-gray-400 italic mb-2"
          numberOfLines={1}
        >
          "{challenge.message}"
        </Text>
      )}

      {/* Footer - time remaining & mode */}
      <View className="flex-row items-center justify-between">
        <View className="flex-row items-center">
          <Ionicons name="time-outline" size={12} color="#9CA3AF" />
          <Text className="ml-1 text-xs text-gray-400">
            {hoursRemaining}u over
          </Text>
        </View>
        <View
          className={`px-2 py-0.5 rounded ${
            challenge.mode === "weekend"
              ? "bg-amber-100 dark:bg-amber-900/30"
              : "bg-purple-100 dark:bg-purple-900/30"
          }`}
        >
          <Text
            className={`text-xs font-medium ${
              challenge.mode === "weekend"
                ? "text-amber-700 dark:text-amber-300"
                : "text-purple-700 dark:text-purple-300"
            }`}
          >
            {challenge.mode === "weekend" ? "🏆" : "🎮"}
          </Text>
        </View>
      </View>
    </TouchableOpacity>
  );
};

export default ChallengeBanner;

